import React, { useMemo } from 'react';
import {
  useHtmlToReact,
  type ElementTransform,
  type NodeTransformContext,
} from '@/components/Common/HtmlToReact';
import { ContentRoot } from './styles';
import ZoomableImage from './ZoomableImage';
import { useImageZoomTrigger } from './ImageZoomModal/context';

type ContentHtmlRenderProps = {
  html: string | null; // 서버에서 sanitize 가 완료된 HTML 문자열
};

/** disabled 처리 대상 type (그 외 readOnly) */
const DISABLE_INPUT_TYPES = new Set([
  'checkbox',
  'radio',
  'submit',
  'button',
  'reset',
]);

const buildApprovalTransform = (
  onZoom: ((src: string) => void) | null,
): ElementTransform => {
  const approvalTransform: ElementTransform = (ctx: NodeTransformContext) => {
    const { tagName, props, element } = ctx;

    switch (tagName) {
      case 'a': {
        props.target = '_blank';
        const existingRel =
          typeof props.rel === 'string' ? (props.rel as string) : '';
        const tokens = new Set(existingRel.split(/\s+/).filter(Boolean));
        tokens.add('noopener');
        tokens.add('noreferrer');
        props.rel = Array.from(tokens).join(' ');
        return undefined;
      }

      case 'input': {
        const type = (element.getAttribute('type') ?? 'text').toLowerCase();
        if (DISABLE_INPUT_TYPES.has(type)) {
          props.disabled = true;
        } else {
          props.readOnly = true;
        }
        return undefined;
      }

      case 'textarea': {
        props.readOnly = true;
        return undefined;
      }

      case 'select': {
        props.disabled = true;
        return undefined;
      }

      case 'form': {
        props.onSubmit = (e: React.FormEvent) => e.preventDefault();
        return undefined;
      }

      case 'button': {
        const type = (element.getAttribute('type') ?? '').toLowerCase();
        if (type === 'submit') {
          props.disabled = true;
        }
        return undefined;
      }

      case 'img': {
        const src = element.getAttribute('src') || '';
        if (onZoom && src) {
          // src/alt 외 나머지 매핑된 속성(className/style 등)은 imgProps 로 전달.
          const { key, ...rest } = props;
          const imgProps = rest as React.ImgHTMLAttributes<HTMLImageElement>;
          const alt = element.getAttribute('alt') ?? undefined;
          // 정렬은 img 가 아니라 감싸는 wrapper(ZoomImageWrap)에 걸어야 먹는다(img margin auto 는 inline-block wrapper 안에선 무효).
          const align = element.getAttribute('data-format') ?? undefined;
          return (
            <ZoomableImage
              key={key as React.Key}
              src={src}
              alt={alt}
              imgProps={imgProps}
              align={align}
              onZoom={onZoom}
            />
          );
        }
        return undefined;
      }

      default:
        return undefined;
    }
  };

  return approvalTransform;
};

/** 본문 HTML 렌더 — read-only 안전처리와 이미지 크게보기를 buildApprovalTransform 으로 반영 */
const ContentHtmlRender = ({ html }: ContentHtmlRenderProps) => {
  // Approval 전용 컴포넌트라 직접 trigger 구독
  const onZoom = useImageZoomTrigger();
  const transform = useMemo(() => buildApprovalTransform(onZoom), [onZoom]);
  const rendered = useHtmlToReact(html, { transform });

  // null/빈 문자열인 경우에도 빈 박스를 노출해 레이아웃 점프 방지.
  return <ContentRoot>{rendered}</ContentRoot>;
};

export default ContentHtmlRender;
